import * as React from "react";
import { As, Box, Grid, Heading, Icon, Link, LinkProps, Text, BoxProps } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";

export const CONTENT_WIDTH = "880px";

// Props for a Chakra Link: routed in-app, or opened in a new tab when external.
export const linkProps = (to: string, external?: boolean, component: As = RouterLink): LinkProps =>
  external ? { href: to, isExternal: true } : ({ as: component, to } as LinkProps);

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

// Staggered fade-up used for page entrances.
export const riseIn = (step: number) => ({
  animation: `${rise} 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both`,
  animationDelay: `${step * 70}ms`,
});

interface PageProps {
  title: React.ReactNode;
  intro?: React.ReactNode;
  children: React.ReactNode;
}

export const Page = ({ title, intro, children }: PageProps) => (
  <Box maxW={CONTENT_WIDTH} mx="auto" px={6} pt={{ base: 12, md: 20 }} pb={{ base: 16, md: 24 }}>
    <Box mb={{ base: 10, md: 14 }} sx={riseIn(0)}>
      <Heading as="h1" fontSize={{ base: "5xl", md: "6xl" }} lineHeight={1}>
        {title}
      </Heading>
      {intro && (
        <Box mt={4} color="muted" maxW="60ch">
          {intro}
        </Box>
      )}
    </Box>
    <Box sx={riseIn(1)}>{children}</Box>
  </Box>
);

interface SectionProps extends BoxProps {
  label: React.ReactNode;
}

export const Section = ({ label, children, ...props }: SectionProps) => (
  <Grid
    as="section"
    templateColumns={{ base: "1fr", md: "9rem 1fr" }}
    columnGap={8}
    rowGap={4}
    py={{ base: 8, md: 10 }}
    borderTop="1px solid"
    borderColor="line"
    {...props}
  >
    <Text
      fontFamily="mono"
      fontSize="xs"
      textTransform="uppercase"
      letterSpacing="0.08em"
      color="muted"
      pt={{ md: 2 }}
    >
      {label}
    </Text>
    <Box minW={0}>{children}</Box>
  </Grid>
);

export const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Heading as="h2" fontSize={{ base: "2xl", md: "3xl" }} lineHeight={1.15} mb={5}>
    {children}
  </Heading>
);

interface PillLinkProps {
  to: string;
  external?: boolean;
  icon?: React.ReactElement;
  children: React.ReactNode;
}

export const PillLink = ({ to, external, icon, children }: PillLinkProps) => {
  return (
    <Link
      {...linkProps(to, external)}
      data-group
      display="inline-flex"
      alignItems="center"
      gap={2}
      px={4}
      py={1.5}
      fontSize="sm"
      border="1px solid"
      borderColor="line"
      borderRadius="full"
      _hover={{ borderColor: "accent", color: "accent" }}
    >
      {icon}
      {children}
      <Icon
        as={external ? FiArrowUpRight : FiArrowRight}
        transition="transform 0.2s ease-out"
        _groupHover={{ transform: "translateX(2px)" }}
      />
    </Link>
  );
};
